import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import NotificationBell from './NotificationBell';
import ChatBadge from './ChatBadge';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';

/* ─── 역할 레벨 ─── */
type MinRole = 'public' | 'USER' | 'AGENT' | 'ADMIN';
const ROLE_LEVEL: Record<string, number> = { public: 0, USER: 1, AGENT: 2, ADMIN: 3 };

interface NavItem {
  to: string;
  label: string;
  desc: string;
  accent?: boolean;
  highlight?: 'blue';
  minRole?: MinRole;
}

interface NavGroup {
  label: string;
  minRole: MinRole;
  items: NavItem[];
}

/* ─── 메뉴 구조 (역할별 접근 제어) ─── */
const NAV_GROUPS: NavGroup[] = [
  {
    label: '매물',
    minRole: 'public',
    items: [
      { to: '/properties', label: '매물 검색', desc: '지도/목록 통합 검색', highlight: 'blue', minRole: 'public' },
      { to: '/properties/undervalued', label: '저평가 매물', desc: '호가 대비 저평가 탐지', accent: true, minRole: 'USER' },
      { to: '/saved-searches/filters', label: '저장된 검색', desc: '조건 저장 + 매물 알림', minRole: 'USER' },
    ],
  },
  {
    label: '분석',
    minRole: 'USER',
    items: [
      { to: '/analysis/market', label: '시장 분석', desc: '가격 추이·거래량·수급' },
      { to: '/analysis/backtest', label: '수익률 백테스팅', desc: '과거 보유기간별 CAGR' },
      { to: '/analysis/gap', label: '호가 vs 실거래 갭', desc: '괴리율 분석' },
      { to: '/analysis/appreciation', label: '상승률 분석', desc: '단지별/지역별 상승률 랭킹' },
      { to: '/analysis/ranking', label: '투자 점수 랭킹', desc: '종합 투자 등급 순위', accent: true },
      { to: '/analysis/contagion', label: '연쇄 패턴', desc: '단지 간 가격 전파 분석', minRole: 'AGENT' },
      { to: '/compare', label: '단지 비교 분석', desc: '10차원 AI 심층 비교', accent: true },
    ],
  },
  {
    label: '포트폴리오',
    minRole: 'USER',
    items: [
      { to: '/portfolio', label: '포트폴리오 대시보드', desc: '관심매물 통합 관리', accent: true },
      { to: '/saved-properties', label: '관심 매물', desc: '저장한 매물 관리' },
      { to: '/price-alerts', label: '가격 알림', desc: 'AI 목표가 알림' },
    ],
  },
  {
    label: 'AI',
    minRole: 'USER',
    items: [
      { to: '/ai-chat', label: 'AI 부동산 챗봇', desc: 'DB 기반 실시간 질의응답', accent: true },
      { to: '/ai-advisor', label: 'AI 투자 어드바이저', desc: '맞춤 매물 추천 + 시나리오', minRole: 'AGENT' },
      { to: '/recommendations', label: 'AI 맞춤 추천', desc: '선호 조건 기반 매물 추천' },
      { to: '/engines', label: '5대 분석 엔진', desc: '금융·교통·환경·인프라·투자' },
      { to: '/simulation', label: '투자 시뮬레이션', desc: 'ROI·NPV·IRR 산출' },
      { to: '/consultation', label: 'AI 상담', desc: '대화형 맞춤 분석', minRole: 'AGENT' },
      { to: '/reports', label: '프리미엄 리포트', desc: '종합 분석 PDF', minRole: 'AGENT' },
    ],
  },
  {
    label: '도구',
    minRole: 'public',
    items: [
      { to: '/tools/calculators', label: '부동산 계산기', desc: '대출·세금·전월세 전환', minRole: 'public' },
      { to: '/education/schools', label: '학군 정보', desc: '동탄 학교·학원 현황', minRole: 'USER' },
    ],
  },
  {
    label: '더보기',
    minRole: 'USER',
    items: [
      { to: '/policies', label: '정책 모니터', desc: '부동산 정책 영향 분석' },
      { to: '/community', label: '커뮤니티', desc: '게시판·동네 리뷰' },
      { to: '/dongtan', label: '동탄 정보', desc: '개발 계획·인프라 지도' },
      { to: '/insights', label: '인사이트', desc: 'YouTube 부동산 뉴스' },
      { to: '/cafe-insights', label: '카페 인사이트', desc: '동탄맘 카페 부동산 분석' },
      { to: '/agents', label: '중개사 찾기', desc: '지역 전문 중개사' },
      { to: '/agents/apply', label: '중개사 등록', desc: '중개사 신청하기', minRole: 'AGENT' },
    ],
  },
];

/** 사용자 역할에 따라 보이는 메뉴 그룹 + 아이템 필터링 */
function getVisibleGroups(role: string | undefined, isAuthenticated: boolean): NavGroup[] {
  const userLevel = isAuthenticated ? (ROLE_LEVEL[role || 'USER'] ?? 1) : 0;
  return NAV_GROUPS
    .filter(g => userLevel >= ROLE_LEVEL[g.minRole])
    .map(g => ({
      ...g,
      items: g.items.filter(item => userLevel >= ROLE_LEVEL[item.minRole || g.minRole]),
    }));
}

export default function Header() {
  const { isAuthenticated, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const visibleGroups = getVisibleGroups(user?.role, isAuthenticated);

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur print:hidden dark:border-gray-800 dark:bg-gray-900/95">
      <div className="container-app flex h-16 items-center justify-between">
        {/* 로고 + 전체메뉴 */}
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center">
            <Logo />
          </Link>
        </div>

        {/* 데스크탑 네비게이션 */}
        <nav className="hidden items-center gap-0.5 lg:flex">
          {visibleGroups.map((group) => (
            <NavDropdown key={group.label} label={group.label} items={group.items} />
          ))}
        </nav>

        {/* 우측 아이콘/버튼 */}
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {isAuthenticated ? (
            <>
              <Link
                to={user?.role === 'ADMIN' ? '/admin/dashboard' : user?.role === 'AGENT' ? '/agents/dashboard' : '/dashboard'}
                className="hidden items-center gap-1.5 rounded-lg bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-100 xl:flex"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                대시보드
              </Link>
              <Link
                to="/sitemap"
                className="hidden items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 xl:flex"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                전체메뉴
              </Link>
              <Link
                to="/saved-properties"
                className="hidden rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 sm:block"
                aria-label="관심 매물"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </Link>
              <Link
                to="/price-alerts"
                className="hidden rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 sm:block"
                aria-label="가격 알림"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </Link>
              <ChatBadge />
              <NotificationBell />
              <ProfileDropdown user={user} onLogout={handleLogout} />
            </>
          ) : (
            <>
              <Link
                to="/sitemap"
                className="hidden items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 xl:flex"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                전체메뉴
              </Link>
              <Link
                to="/login"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                로그인
              </Link>
              <Link
                to="/register"
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                회원가입
              </Link>
            </>
          )}

          {/* 모바일 햄버거 */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 lg:hidden"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* 모바일 메뉴 */}
      {mobileOpen && (
        <div className="border-t border-gray-100 bg-white px-4 py-3 lg:hidden">
          <nav className="flex flex-col gap-1">
            <Link
              to="/sitemap"
              className="mb-2 flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
              onClick={() => setMobileOpen(false)}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              전체 메뉴 보기
            </Link>
            {visibleGroups.map((group) => (
              <MobileGroup
                key={group.label}
                label={group.label}
                items={group.items}
                onNavigate={() => setMobileOpen(false)}
              />
            ))}
            {isAuthenticated && (
              <>
                <div className="my-2 border-t border-gray-100" />
                <Link
                  to={user?.role === 'ADMIN' ? '/admin/dashboard' : user?.role === 'AGENT' ? '/agents/dashboard' : '/dashboard'}
                  className="rounded-lg bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100"
                  onClick={() => setMobileOpen(false)}
                >
                  대시보드
                </Link>
                <Link
                  to="/saved-properties"
                  className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                  onClick={() => setMobileOpen(false)}
                >
                  관심 매물
                </Link>
                <Link
                  to="/price-alerts"
                  className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                  onClick={() => setMobileOpen(false)}
                >
                  가격 알림
                </Link>
                <Link
                  to="/notifications"
                  className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                  onClick={() => setMobileOpen(false)}
                >
                  알림
                </Link>
                {user?.role === 'AGENT' && (
                  <Link
                    to="/admin/naver"
                    className="rounded-lg bg-green-50 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-100"
                    onClick={() => setMobileOpen(false)}
                  >
                    매물 관리
                  </Link>
                )}
                {user?.role === 'ADMIN' && (
                  <>
                    <Link
                      to="/admin/integrations"
                      className="rounded-lg bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100"
                      onClick={() => setMobileOpen(false)}
                    >
                      연동 모니터링
                    </Link>
                    <Link
                      to="/admin/agent-approvals"
                      className="rounded-lg bg-purple-50 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100"
                      onClick={() => setMobileOpen(false)}
                    >
                      중개사 심사
                    </Link>
                  </>
                )}
                <Link
                  to="/profile"
                  className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                  onClick={() => setMobileOpen(false)}
                >
                  내 프로필
                </Link>
                <button
                  onClick={() => { handleLogout(); setMobileOpen(false); }}
                  className="rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  로그아웃
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

/* ─── 데스크탑 드롭다운 ─── */
function NavDropdown({ label, items }: {
  label: string;
  items: NavItem[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = () => {
    clearTimeout(timeout.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeout.current = setTimeout(() => setOpen(false), 150);
  };

  // 외부 클릭 닫기
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-0.5 rounded-lg px-2 py-1.5 text-sm font-medium transition ${
          open ? 'bg-gray-100 text-primary-600 dark:bg-gray-800 dark:text-primary-400' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
        }`}
      >
        {label}
        <svg className={`h-3 w-3 transition ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-xl border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-700 dark:bg-gray-900">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={`block px-4 py-2.5 transition hover:bg-gray-50 dark:hover:bg-gray-800 ${
                item.accent ? 'hover:bg-red-50 dark:hover:bg-red-950/30' : ''
              } ${item.highlight === 'blue' ? 'hover:bg-blue-50 dark:hover:bg-blue-950/30' : ''}`}
            >
              <span className={`text-[13px] ${
                item.highlight === 'blue' ? 'font-bold text-blue-600 dark:text-blue-400' : item.accent ? 'font-medium text-red-600 dark:text-red-400' : 'font-medium text-gray-900 dark:text-gray-100'
              }`}>
                {item.label}
              </span>
              <span className="block text-xs text-gray-400 dark:text-gray-500">{item.desc}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── 프로필 드롭다운 (로그아웃 포함) ─── */
function ProfileDropdown({ user, onLogout }: { user: any; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = () => { clearTimeout(timeout.current); setOpen(true); };
  const handleLeave = () => { timeout.current = setTimeout(() => setOpen(false), 150); };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const roleLabel = user?.role === 'ADMIN' ? '관리자' : user?.role === 'AGENT' ? '중개사' : '';

  return (
    <div ref={ref} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
      >
        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
          user?.role === 'ADMIN' ? 'bg-purple-100 text-purple-600' : 'bg-primary-100 text-primary-600'
        }`}>
          {user?.name?.charAt(0) || 'U'}
        </span>
        <span className="hidden xl:inline">{user?.name}</span>
        {roleLabel && roleLabel !== user?.name && (
          <span className={`hidden text-[10px] font-semibold xl:inline ${
            user?.role === 'ADMIN' ? 'text-purple-500' : 'text-green-500'
          }`}>
            {roleLabel}
          </span>
        )}
        <svg className={`h-3 w-3 text-gray-400 transition ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            내 프로필
          </Link>
          {user?.role === 'ADMIN' && (
            <Link
              to="/admin/integrations"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              연동 모니터링
            </Link>
          )}
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            로그아웃
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── 모바일 아코디언 그룹 ─── */
function MobileGroup({ label, items, onNavigate }: {
  label: string;
  items: NavItem[];
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-50"
      >
        {label}
        <svg className={`h-4 w-4 text-gray-400 transition ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="ml-3 flex flex-col gap-0.5 border-l-2 border-gray-100 pl-3">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={`rounded-lg px-3 py-2 text-[15px] ${
                item.highlight === 'blue'
                  ? 'font-bold text-blue-600 hover:bg-blue-50'
                  : item.accent
                    ? 'font-medium text-red-600 hover:bg-red-50'
                    : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
